import React, { useMemo, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Check, Copy } from 'lucide-react';
import { PrismLight as SyntaxHighlighter } from 'react-syntax-highlighter';
import oneDark from 'react-syntax-highlighter/dist/esm/styles/prism/one-dark';
import oneLight from 'react-syntax-highlighter/dist/esm/styles/prism/one-light';
import bash from 'react-syntax-highlighter/dist/esm/languages/prism/bash';
import csharp from 'react-syntax-highlighter/dist/esm/languages/prism/csharp';
import css from 'react-syntax-highlighter/dist/esm/languages/prism/css';
import diff from 'react-syntax-highlighter/dist/esm/languages/prism/diff';
import docker from 'react-syntax-highlighter/dist/esm/languages/prism/docker';
import go from 'react-syntax-highlighter/dist/esm/languages/prism/go';
import java from 'react-syntax-highlighter/dist/esm/languages/prism/java';
import javascript from 'react-syntax-highlighter/dist/esm/languages/prism/javascript';
import json from 'react-syntax-highlighter/dist/esm/languages/prism/json';
import jsx from 'react-syntax-highlighter/dist/esm/languages/prism/jsx';
import markdown from 'react-syntax-highlighter/dist/esm/languages/prism/markdown';
import markup from 'react-syntax-highlighter/dist/esm/languages/prism/markup';
import php from 'react-syntax-highlighter/dist/esm/languages/prism/php';
import python from 'react-syntax-highlighter/dist/esm/languages/prism/python';
import rust from 'react-syntax-highlighter/dist/esm/languages/prism/rust';
import scss from 'react-syntax-highlighter/dist/esm/languages/prism/scss';
import sql from 'react-syntax-highlighter/dist/esm/languages/prism/sql';
import tsx from 'react-syntax-highlighter/dist/esm/languages/prism/tsx';
import typescript from 'react-syntax-highlighter/dist/esm/languages/prism/typescript';
import yaml from 'react-syntax-highlighter/dist/esm/languages/prism/yaml';
import { classNames } from '../../utils/classNames';
import { useI18n } from '../../hooks/useI18n';
import { useAppSelector } from '../../hooks/useAppRedux';
import { selectTheme } from '../../redux/slices/appSlice';
import { copyTextToClipboard } from './MessageActions';

SyntaxHighlighter.registerLanguage('bash', bash);
SyntaxHighlighter.registerLanguage('sh', bash);
SyntaxHighlighter.registerLanguage('shell', bash);
SyntaxHighlighter.registerLanguage('zsh', bash);
SyntaxHighlighter.registerLanguage('csharp', csharp);
SyntaxHighlighter.registerLanguage('cs', csharp);
SyntaxHighlighter.registerLanguage('css', css);
SyntaxHighlighter.registerLanguage('diff', diff);
SyntaxHighlighter.registerLanguage('docker', docker);
SyntaxHighlighter.registerLanguage('dockerfile', docker);
SyntaxHighlighter.registerLanguage('go', go);
SyntaxHighlighter.registerLanguage('java', java);
SyntaxHighlighter.registerLanguage('javascript', javascript);
SyntaxHighlighter.registerLanguage('js', javascript);
SyntaxHighlighter.registerLanguage('json', json);
SyntaxHighlighter.registerLanguage('jsx', jsx);
SyntaxHighlighter.registerLanguage('markdown', markdown);
SyntaxHighlighter.registerLanguage('md', markdown);
SyntaxHighlighter.registerLanguage('markup', markup);
SyntaxHighlighter.registerLanguage('html', markup);
SyntaxHighlighter.registerLanguage('xml', markup);
SyntaxHighlighter.registerLanguage('php', php);
SyntaxHighlighter.registerLanguage('python', python);
SyntaxHighlighter.registerLanguage('py', python);
SyntaxHighlighter.registerLanguage('rust', rust);
SyntaxHighlighter.registerLanguage('scss', scss);
SyntaxHighlighter.registerLanguage('sql', sql);
SyntaxHighlighter.registerLanguage('tsx', tsx);
SyntaxHighlighter.registerLanguage('typescript', typescript);
SyntaxHighlighter.registerLanguage('ts', typescript);
SyntaxHighlighter.registerLanguage('yaml', yaml);
SyntaxHighlighter.registerLanguage('yml', yaml);

const LANGUAGE_ALIASES = {
  js: 'javascript',
  ts: 'typescript',
  py: 'python',
  sh: 'bash',
  shell: 'bash',
  zsh: 'bash',
  yml: 'yaml',
  html: 'markup',
  xml: 'markup',
  md: 'markdown',
  cs: 'csharp',
  'c#': 'csharp',
  dockerfile: 'docker'
};

const REGISTERED_LANGUAGES = new Set([
  'bash',
  'csharp',
  'css',
  'diff',
  'docker',
  'go',
  'java',
  'javascript',
  'json',
  'jsx',
  'markdown',
  'markup',
  'php',
  'python',
  'rust',
  'scss',
  'sql',
  'tsx',
  'typescript',
  'yaml'
]);

const extractText = (node) => {
  if (node == null || typeof node === 'boolean') return '';
  if (typeof node === 'string' || typeof node === 'number') return String(node);
  if (Array.isArray(node)) return node.map(extractText).join('');
  if (React.isValidElement(node)) return extractText(node.props?.children);
  return '';
};

const getLanguage = (className = '') => {
  const match = /(?:^|\s)language-([\w+-]+)/.exec(className);
  return match?.[1]?.toLowerCase() || '';
};

const resolveLanguage = (language) => {
  if (!language) return '';
  const aliased = LANGUAGE_ALIASES[language] || language;
  return REGISTERED_LANGUAGES.has(aliased) ? aliased : '';
};

const CodeBlock = ({ children }) => {
  const { t } = useI18n();
  const theme = useAppSelector(selectTheme);
  const isDark = theme === 'dark';
  const [copied, setCopied] = useState(false);
  const codeElement = React.Children.toArray(children).find((child) => React.isValidElement(child));
  const language = getLanguage(codeElement?.props?.className || '');
  const highlightLanguage = resolveLanguage(language);
  const codeText = extractText(children).replace(/\n$/, '');

  const highlighterStyle = useMemo(() => {
    const base = isDark ? oneDark : oneLight;
    return {
      ...base,
      'pre[class*="language-"]': {
        ...base['pre[class*="language-"]'],
        margin: 0,
        padding: 0,
        background: 'transparent'
      },
      'code[class*="language-"]': {
        ...base['code[class*="language-"]'],
        background: 'transparent',
        textShadow: 'none'
      }
    };
  }, [isDark]);

  const handleCopy = async (event) => {
    event.preventDefault();
    event.stopPropagation();
    await copyTextToClipboard(codeText);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1500);
  };

  return (
    <div
      className={classNames(
        'my-3 overflow-hidden rounded-xl border text-start shadow-sm',
        isDark
          ? 'border-neutral-800 bg-[#21252b]'
          : 'border-neutral-200 bg-[#fafafa]'
      )}
      dir="ltr"
    >
      <div
        className={classNames(
          'flex items-center justify-between gap-2 border-b px-3 py-1.5',
          isDark ? 'border-neutral-700/80 bg-[#282c34]' : 'border-neutral-200 bg-neutral-100/90'
        )}
      >
        <span
          className={classNames(
            'truncate text-[11px] font-medium uppercase tracking-wide',
            isDark ? 'text-neutral-400' : 'text-neutral-500'
          )}
        >
          {language || 'code'}
        </span>
        <button
          type="button"
          onClick={handleCopy}
          title={copied ? t('chatCopied') : t('chatCopy')}
          aria-label={copied ? t('chatCopied') : t('chatCopy')}
          className={classNames(
            'inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-[11px] font-medium transition',
            isDark
              ? 'text-neutral-400 hover:bg-neutral-700/70 hover:text-neutral-100'
              : 'text-neutral-500 hover:bg-neutral-200/80 hover:text-neutral-800',
            copied && (isDark ? 'text-emerald-400 hover:text-emerald-400' : 'text-emerald-600 hover:text-emerald-600')
          )}
        >
          {copied ? <Check size={12} /> : <Copy size={12} />}
          <span>{copied ? t('chatCopied') : t('chatCopy')}</span>
        </button>
      </div>
      <div className="overflow-x-auto p-3.5">
        {highlightLanguage ? (
          <SyntaxHighlighter
            language={highlightLanguage}
            style={highlighterStyle}
            PreTag="div"
            customStyle={{
              margin: 0,
              padding: 0,
              background: 'transparent',
              fontSize: '0.85em',
              lineHeight: 1.65
            }}
            codeTagProps={{
              style: {
                fontFamily:
                  'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
                background: 'transparent',
                textShadow: 'none'
              }
            }}
          >
            {codeText}
          </SyntaxHighlighter>
        ) : (
          <pre className="m-0 text-left">
            <code
              className={classNames(
                'block whitespace-pre font-mono text-[0.85em] leading-6',
                isDark ? 'text-neutral-100' : 'text-neutral-800'
              )}
            >
              {codeText}
            </code>
          </pre>
        )}
      </div>
    </div>
  );
};

const components = {
  p: ({ children }) => <p className="my-2.5 first:mt-0 last:mb-0 leading-8">{children}</p>,
  ul: ({ children }) => <ul className="my-2.5 list-disc space-y-1 ps-5 marker:text-neutral-400">{children}</ul>,
  ol: ({ children }) => <ol className="my-2.5 list-decimal space-y-1 ps-5 marker:text-neutral-400">{children}</ol>,
  li: ({ children }) => <li className="leading-8 [&>ul]:mt-1 [&>ol]:mt-1">{children}</li>,
  h1: ({ children }) => <h1 className="mb-2 mt-5 text-2xl font-semibold tracking-tight first:mt-0">{children}</h1>,
  h2: ({ children }) => <h2 className="mb-2 mt-4 text-xl font-semibold tracking-tight first:mt-0">{children}</h2>,
  h3: ({ children }) => <h3 className="mb-1.5 mt-3.5 text-lg font-semibold first:mt-0">{children}</h3>,
  strong: ({ children }) => <strong className="font-semibold text-neutral-950 dark:text-white">{children}</strong>,
  em: ({ children }) => <em className="italic">{children}</em>,
  a: ({ href, children }) => {
    const safeHref =
      typeof href === 'string' && (/^https?:\/\//i.test(href) || href.startsWith('/'))
        ? href
        : undefined;
    return (
      <a
        href={safeHref}
        target="_blank"
        rel="noopener noreferrer"
        className="font-medium text-blue-600 underline decoration-blue-600/30 underline-offset-2 transition hover:decoration-blue-600 dark:text-blue-400 dark:decoration-blue-400/30 dark:hover:decoration-blue-400"
      >
        {children}
      </a>
    );
  },
  // Inline code only — fenced blocks are handled by `pre` / CodeBlock.
  code: ({ className, children }) => {
    const language = getLanguage(className || '');
    const text = extractText(children);
    // Fenced blocks are re-rendered inside CodeBlock; keep raw props for extraction.
    if (language || text.includes('\n')) {
      return <code className={className}>{children}</code>;
    }
    return (
      <code
        dir="ltr"
        className="rounded-md bg-neutral-100 px-1.5 py-0.5 font-mono text-[0.88em] text-neutral-800 dark:bg-neutral-800 dark:text-neutral-200"
      >
        {children}
      </code>
    );
  },
  pre: ({ children }) => <CodeBlock>{children}</CodeBlock>,
  blockquote: ({ children }) => (
    <blockquote className="my-3 border-s-2 border-neutral-300 ps-3 text-neutral-500 dark:border-neutral-600 dark:text-neutral-400">
      {children}
    </blockquote>
  ),
  table: ({ children }) => (
    <div className="my-3 overflow-x-auto rounded-lg border border-neutral-200 dark:border-neutral-700">
      <table className="w-full border-collapse text-base">{children}</table>
    </div>
  ),
  thead: ({ children }) => <thead className="bg-neutral-50 dark:bg-neutral-900">{children}</thead>,
  th: ({ children }) => (
    <th className="border-b border-neutral-200 px-3 py-2 text-start font-semibold dark:border-neutral-700">{children}</th>
  ),
  td: ({ children }) => (
    <td className="border-b border-neutral-100 px-3 py-2 align-top dark:border-neutral-800">{children}</td>
  ),
  hr: () => <hr className="my-5 border-neutral-200 dark:border-neutral-800" />
};

export const MessageMarkdown = ({ content, className = '' }) => {
  if (!content) return null;

  return (
    <div
      className={classNames(
        'break-words text-base leading-8 text-neutral-900 dark:text-neutral-100',
        '[&>*:first-child]:mt-0 [&>*:last-child]:mb-0',
        className
      )}
    >
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>
        {content}
      </ReactMarkdown>
    </div>
  );
};
